// components/CustomerForm.js
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Phone, Mail, Building, FileText, DollarSign, AlertCircle, Save, Calendar, Tag, MapPin, Home } from 'lucide-react';
import { getCustomerStages } from '../utils/customerStages';
import { openDb, idbGetCustomer, idbPutCustomer } from '../utils/idb';
import { ensureHistoryInitialized } from '../hooks/useCustomerHistory';
import { getModalVariants, overlayVariants, buttonVariants } from '../utils/animationUtils';
import { geocodeCustomer, isCustomerGeocoded } from '../utils/geocoding';
import { generateDeviceId } from '../utils/deviceUtils';

const CustomerForm = ({
    editingCustomer,
    targetStageForAdd,
    customers,
    setCustomers,
    addCustomer,
    updateCustomer,
    sendActionLog,
    onClose,
    businessType = 'insurance',
    appStatus
}) => {
    const CUSTOMER_STAGES = getCustomerStages(businessType);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        company: '',
        birthdate: '',
        memo: '',
        value: '',
        priority: 'medium',
        keyword: '',
        address: '',
        detailAddress: '',
        relation: ''
    });

    const [errors, setErrors] = useState({});
    const [showCelebration, setShowCelebration] = useState(false);

    // 숫자 천단위 포맷
    const formatCurrencyInput = (input) => {
        const digits = String(input || '').replace(/[^0-9]/g, '');
        if (!digits) return '';
        return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    // 휴대폰 하이픈 포맷 (간단 규칙: 3-3/4-4)
    const formatPhoneInput = (input) => {
        const digits = String(input || '').replace(/[^0-9]/g, '');
        if (digits.length <= 3) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
    };

    // 생년월일 포맷팅 (YYYY-MM-DD)
    const formatBirthdateInput = (input) => {
        const digits = String(input || '').replace(/[^0-9]/g, '');
        if (digits.length <= 4) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
        return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    };

    useEffect(() => {
        if (editingCustomer) {
            setFormData({
                name: editingCustomer.name || '',
                phone: formatPhoneInput(editingCustomer.phone || ''),
                email: editingCustomer.email || '',
                company: editingCustomer.company || '',
                birthdate: editingCustomer.birthdate || '',
                memo: editingCustomer.memo || '',
                value: editingCustomer.value ? formatCurrencyInput(editingCustomer.value) : '',
                priority: editingCustomer.priority || 'medium',
                keyword: editingCustomer.keyword || '',
                address: editingCustomer.address || '',
                detailAddress: editingCustomer.detailAddress || '',
                relation: editingCustomer.relation || ''
            });
        }
    }, [editingCustomer]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = '이름을 입력해주세요';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = '연락처를 입력해주세요';
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 만료 상태일 때 누적 고객 등록수 제한 체크 (새 고객 추가 시에만)
        if (appStatus === 'expired' && !editingCustomer) {
            const { tryAddExpiredCustomer, getExpiredCustomerCount } = await import('../utils/expiredActionLimit');
            const handleLimitExceeded = async () => {
                const count = await getExpiredCustomerCount();
                alert(`체험 기간이 만료되어 누적 10명까지만 등록할 수 있습니다.\n현재 ${count}명 등록되어 있습니다.\n계속 사용하려면 사용신청을 해주세요.`);
                // alert 후 스크롤 잠금 복원
                setTimeout(() => {
                    const scrollY = window.scrollY;
                    document.body.style.position = 'fixed';
                    document.body.style.top = `-${scrollY}px`;
                    document.body.style.width = '100%';
                    document.body.style.overflow = 'hidden';
                }, 50);
                try {
                    window.dispatchEvent(new Event('openApprovalModal'));
                } catch (err) {
                    console.warn('[CustomerForm] dispatch failed', err);
                }
            };

            const executed = await tryAddExpiredCustomer(
                async () => {
                    // 실제 submit 로직은 아래에서 계속 실행됨
                },
                handleLimitExceeded
            );
            if (!executed) return;
            // executed가 true면 아래 로직 계속 실행
        }

        if (!validateForm()) {
            return;
        }

        const numericValue = parseInt(String(formData.value).replace(/[^0-9]/g, ''), 10) || 0;

        const customerData = {
            ...formData,
            value: numericValue,
            id: editingCustomer ? editingCustomer.id : crypto.randomUUID(),
            isSample: false
        };

        if (editingCustomer) {
            // 기존 고객 수정
            let targetStage = '';
            Object.keys(customers).forEach(stage => {
                if ((customers[stage] || []).some(c => c.id === editingCustomer.id)) {
                    targetStage = stage;
                }
            });

            if (targetStage) {
                try {
                    // IndexedDB에 저장
                    const customerWithStage = { ...customerData, stage: targetStage };
                    await updateCustomer(editingCustomer.id, customerWithStage);

                    // React 상태 업데이트
                    const newCustomers = {
                        ...customers,
                        [targetStage]: (customers[targetStage] || []).map(c =>
                            c.id === editingCustomer.id ? customerWithStage : c
                        )
                    };
                    setCustomers(newCustomers);

                    // 히스토리 초기화
                    ensureHistoryInitialized(customerData.id);

                    await sendActionLog('고객_수정', {
                        customerName: customerData.name,
                        targetStage: CUSTOMER_STAGES.find(s => s.id === targetStage)?.title || targetStage
                    });

                    // 백그라운드 지오코딩 (주소가 있고 좌표가 없는 경우)
                    if (customerWithStage.address && customerWithStage.address.trim() !== '' && !isCustomerGeocoded(customerWithStage)) {
                        try {
                            const geocodedCustomer = await geocodeCustomer(customerWithStage);
                            // 원본 고객 데이터에 좌표만 추가하여 안전하게 업데이트
                            const updatedCustomer = {
                                ...customerWithStage,
                                lat: geocodedCustomer.lat,
                                lng: geocodedCustomer.lng,
                                geocodedAt: geocodedCustomer.geocodedAt
                            };
                            await idbPutCustomer(updatedCustomer);

                            // React 상태도 함께 업데이트
                            setCustomers(prev => {
                                const newCustomers = { ...prev };
                                Object.keys(newCustomers).forEach(stage => {
                                    newCustomers[stage] = newCustomers[stage].map(customer =>
                                        customer.id === customerWithStage.id ? updatedCustomer : customer
                                    );
                                });
                                return newCustomers;
                            });

                            console.log(`고객 ${customerWithStage.name} 지오코딩 완료`);
                        } catch (geocodeError) {
                            console.warn(`고객 ${customerWithStage.name} 지오코딩 실패:`, geocodeError);
                            // 지오코딩 실패해도 고객 수정은 성공으로 처리
                        }
                    }

                    // 고객 수정 완료 알림
                    alert(`${customerData.name}님의 정보가 성공적으로 수정되었습니다!`);

                    // GA4 커스텀 이벤트: 리드 정보 업데이트
                    try {
                        const gtag = window.gtag;
                        const measurementId = window.GA_MEASUREMENT_ID;
                        if (typeof gtag === 'function' && measurementId) {
                            const deviceId = generateDeviceId();
                            gtag('event', 'lead_update', {
                                target_stage: targetStage,
                                priority: customerData.priority || 'medium',
                                device_id: deviceId
                            });
                        }
                    } catch (_) {}
                } catch (error) {
                    console.error('고객 수정 실패:', error);
                    alert('고객 수정 중 오류가 발생했습니다.');
                    return;
                }
            }
        } else {
            // 새 고객 추가 - IndexedDB에 직접 저장
            const stage = targetStageForAdd || 'prospect';
            const customerWithStage = { ...customerData, stage, createdAt: new Date().toISOString() };

            try {
                // IndexedDB에 저장
                const created = await addCustomer(customerWithStage);

                // 저장 검증: 즉시 조회하여 확정
                try {
                    await openDb();
                    const saved = await idbGetCustomer(created.id);
                    if (!saved) {
                        console.error('저장 검증 실패: IndexedDB에서 방금 추가한 고객을 찾을 수 없습니다.', created);
                        alert('저장에 실패했습니다. 새로고침 후 다시 시도해주세요.');
                        return;
                    }
                } catch (verifyErr) {
                    console.error('저장 검증 중 오류:', verifyErr);
                    alert('저장에 실패했습니다. 새로고침 후 다시 시도해주세요.');
                    return;
                }

                // React 상태 업데이트
                const sorted = [customerWithStage, ...((customers[stage] || []))]
                    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
                const newCustomers = {
                    ...customers,
                    [stage]: sorted
                };
                setCustomers(newCustomers);

                // GA4 전환: 신규 리드 생성(generate_lead)
                try {
                    const gtag = window.gtag;
                    const measurementId = window.GA_MEASUREMENT_ID;
                    if (typeof gtag === 'function' && measurementId) {
                        const deviceId = generateDeviceId();
                        gtag('event', 'generate_lead', {
                            lead_type: 'customer_add',
                            priority: customerWithStage.priority || 'medium',
                            stage: stage,
                            value: Number(numericValue) || 0,
                            currency: 'KRW',
                            device_id: deviceId
                        });
                    }
                } catch (_) {}

                // 히스토리 초기화
                ensureHistoryInitialized(customerWithStage.id);

                await sendActionLog('고객_추가', {
                    customerName: customerData.name,
                    targetStage: CUSTOMER_STAGES.find(s => s.id === stage)?.title || stage
                });

                // 백그라운드 지오코딩 (주소가 있는 경우)
                if (customerWithStage.address && customerWithStage.address.trim() !== '') {
                    try {
                        const geocodedCustomer = await geocodeCustomer(customerWithStage);
                        // 원본 고객 데이터에 좌표만 추가하여 안전하게 업데이트
                        const updatedCustomer = {
                            ...customerWithStage,
                            lat: geocodedCustomer.lat,
                            lng: geocodedCustomer.lng,
                            geocodedAt: geocodedCustomer.geocodedAt
                        };
                        await idbPutCustomer(updatedCustomer);

                        // React 상태도 함께 업데이트
                        setCustomers(prev => {
                            const newCustomers = { ...prev };
                            if (newCustomers[stage]) {
                                newCustomers[stage] = newCustomers[stage].map(customer =>
                                    customer.id === customerWithStage.id ? updatedCustomer : customer
                                );
                            }
                            return newCustomers;
                        });

                        console.log(`고객 ${customerWithStage.name} 지오코딩 완료`);
                    } catch (geocodeError) {
                        console.warn(`고객 ${customerWithStage.name} 지오코딩 실패:`, geocodeError);
                        // 지오코딩 실패해도 고객 추가는 성공으로 처리
                    }
                }


                // 첫 고객 추가 시 다음 행동 모달을 트리거하기 위해 이벤트 발생
                try {
                    const currentCount = Number(localStorage.getItem('totalCustomersAdded') || '0');
                    const nextCount = currentCount + 1;
                    localStorage.setItem('totalCustomersAdded', String(nextCount));
                    if (nextCount === 1) {
                        window.dispatchEvent(new CustomEvent('openNextAction', { detail: { customer: customerWithStage } }));
                    }
                } catch (_) { }

                // 축하 효과 시작 (모달 닫기는 축하 효과 완료 후 자동으로 처리됨)
                startCelebration();
                return; // onClose() 호출하지 않음 (축하 효과에서 처리)
            } catch (error) {
                console.error('고객 추가 실패:', error);
                alert('고객 추가 중 오류가 발생했습니다.');
                return;
            }
        }

        // 고객 수정의 경우에는 바로 모달 닫기
        onClose();
    };

    const handleInputChange = (field, value) => {
        let next = value;
        if (field === 'phone') {
            next = formatPhoneInput(value);
        } else if (field === 'value') {
            next = formatCurrencyInput(value);
        } else if (field === 'birthdate') {
            next = formatBirthdateInput(value);
        }
        setFormData(prev => ({ ...prev, [field]: next }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // 관계 옵션: 기본값 + 기존 고객 데이터에서 수집
    const relationOptions = useMemo(() => {
        const defaults = ['가망고객', '가족'];
        const set = new Set(defaults);
        try {
            Object.keys(customers || {}).forEach(stage => {
                (customers[stage] || []).forEach(c => {
                    if (c && typeof c.relation === 'string') {
                        const v = c.relation.trim();
                        if (v) set.add(v);
                    }
                });
            });
        } catch (_) { }
        return Array.from(set);
    }, [customers]);

    const [isTypingRelation, setIsTypingRelation] = useState(false);

    // 파티클 효과를 위한 상태
    const [particles, setParticles] = useState([]);

    // 파티클 생성 함수
    const createParticles = () => {
        const newParticles = [];
        for (let i = 0; i < 50; i++) {
            newParticles.push({
                id: i,
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
                vx: (Math.random() - 0.5) * 4,
                vy: (Math.random() - 0.5) * 4,
                size: Math.random() * 8 + 4,
                color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][Math.floor(Math.random() * 6)],
                rotation: Math.random() * 360,
                rotationSpeed: (Math.random() - 0.5) * 10
            });
        }
        setParticles(newParticles);
    };

    // 파티클 애니메이션
    useEffect(() => {
        if (showCelebration && particles.length > 0) {
            const interval = setInterval(() => {
                setParticles(prevParticles =>
                    prevParticles.map(particle => ({
                        ...particle,
                        x: particle.x + particle.vx,
                        y: particle.y + particle.vy,
                        rotation: particle.rotation + particle.rotationSpeed,
                        vy: particle.vy + 0.1 // 중력 효과
                    })).filter(particle => particle.y < window.innerHeight + 100)
                );
            }, 50);

            return () => clearInterval(interval);
        }
    }, [showCelebration, particles.length]);

    // 축하 효과 시작
    const startCelebration = () => {
        setShowCelebration(true);
        createParticles();

        // 2.5초 후 축하 효과 종료 및 모달 닫기
        setTimeout(() => {
            setShowCelebration(false);
            setParticles([]);
            onClose();
        }, 2500);
    };



    // 모달 오픈 시 배경 스크롤 잠금 (모달 내부만 스크롤)
    useEffect(() => {
        // 현재 스크롤 위치 저장 후 body 고정
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';

        return () => {
            // 복구 및 스크롤 위치 원상복귀
            const top = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            if (top) {
                window.scrollTo(0, parseInt(top || '0') * -1);
            }
        };
    }, []);

    const targetStageInfo = targetStageForAdd
        ? CUSTOMER_STAGES.find(stage => stage.id === targetStageForAdd)
        : null;

    return (
        <AnimatePresence>
            {/* 축하 효과 오버레이 */}
            {showCelebration && (
                <motion.div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center"
                    style={{ zIndex: 1200 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* 파티클 효과 */}
                    {particles.map(particle => (
                        <motion.div
                            key={particle.id}
                            className="absolute rounded-full"
                            style={{
                                left: particle.x,
                                top: particle.y,
                                width: particle.size,
                                height: particle.size,
                                backgroundColor: particle.color,
                                boxShadow: `0 0 ${particle.size}px ${particle.color}`,
                            }}
                            initial={{
                                scale: 0,
                                rotate: 0,
                                opacity: 0
                            }}
                            animate={{
                                scale: 1,
                                rotate: 360,
                                opacity: 1,
                                y: [0, -20, 0],
                                x: [0, Math.random() * 20 - 10, 0]
                            }}
                            transition={{
                                duration: 2,
                                ease: "easeOut",
                                delay: Math.random() * 0.5
                            }}
                        />
                    ))}

                    {/* 축하 메시지 */}
                    <motion.div
                        className="text-center"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                            delay: 0.2
                        }}
                    >
                        <motion.div
                            className="text-8xl mb-4"
                            animate={{
                                rotate: [0, -10, 10, -10, 10, 0],
                                scale: [1, 1.1, 1]
                            }}
                            transition={{
                                duration: 0.6,
                                repeat: 3,
                                repeatType: "reverse"
                            }}
                        >
                            🎉
                        </motion.div>
                        <motion.div
                            className="text-4xl font-bold text-white mb-2 drop-shadow-lg"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                        >
                            축하합니다!
                        </motion.div>
                        <motion.div
                            className="text-xl text-white/90 drop-shadow-md"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.6, duration: 0.5 }}
                        >
                            새 고객이 성공적으로 추가되었습니다
                        </motion.div>
                    </motion.div>
                </motion.div>
            )}

            {/* 기존 모달 */}
            <motion.div
                className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4"
                style={{ zIndex: 1100 }}
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
            >
                <motion.div
                    className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full max-h-[90vh] shadow-2xl border border-white/20 overflow-hidden flex flex-col"
                    variants={getModalVariants()}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    {/* 모던 헤더 */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                                    <User className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">
                                        {editingCustomer ? '고객 정보 수정' : '새 고객 추가'}
                                    </h3>
                                    {targetStageInfo && !editingCustomer && (
                                        <p className="text-sm text-gray-600 mt-1">
                                            {targetStageInfo.emoji} {targetStageInfo.title}에 추가
                                        </p>
                                    )}
                                </div>
                            </div>
                            <motion.button
                                onClick={onClose}
                                className="p-2 bg-white/60 rounded-xl hover:bg-white/80 transition-all duration-300"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </motion.button>
                        </div>
                    </div>

                    {/* 폼 내용 - 스크롤 가능 */}
                    <div className="flex-1 overflow-y-auto overscroll-contain">
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* 이름 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    이름 <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${errors.name
                                            ? 'border-red-300 focus:border-red-500 bg-red-50'
                                            : 'border-gray-200 focus:border-blue-500 bg-white'
                                            }`}
                                        placeholder="고객 이름을 입력하세요"
                                        autoFocus
                                    />
                                </div>
                                {errors.name && (
                                    <div className="flex items-center gap-2 text-red-600 text-sm">
                                        <AlertCircle className="w-4 h-4" />
                                        {errors.name}
                                    </div>
                                )}
                            </div>

                            {/* 연락처 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    연락처 <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 ${errors.phone
                                            ? 'border-red-300 focus:border-red-500 bg-red-50'
                                            : 'border-gray-200 focus:border-blue-500 bg-white'
                                            }`}
                                        placeholder="010-1234-5678"
                                    />
                                </div>
                                {errors.phone && (
                                    <div className="flex items-center gap-2 text-red-600 text-sm">
                                        <AlertCircle className="w-4 h-4" />
                                        {errors.phone}
                                    </div>
                                )}
                            </div>

                            <details open={editingCustomer ? true : undefined} className="rounded-xl border border-gray-200 p-3">
                                <summary className="cursor-pointer text-sm font-semibold text-blue-700">
                                    추가 정보 (선택) · 생일, 주소, 회사, 메모 등
                                </summary>
                                <div className="space-y-5 pt-4">
                            {/* 생년월일 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    생년월일 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={formData.birthdate}
                                        onChange={(e) => handleInputChange('birthdate', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        placeholder="1990-01-01"
                                        maxLength="10"
                                    />
                                </div>
                            </div>

                            {/* 주소 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    주소 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        placeholder="기본 주소를 입력하세요"
                                    />
                                </div>
                            </div>

                            {/* 상세주소 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    상세주소 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={formData.detailAddress}
                                        onChange={(e) => handleInputChange('detailAddress', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        placeholder="상세 주소를 입력하세요"
                                    />
                                </div>
                            </div>

                            {/* 회사명 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    회사명 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={formData.company}
                                        onChange={(e) => handleInputChange('company', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        placeholder="회사명을 입력하세요"
                                    />
                                </div>
                            </div>

                            {/* 이메일 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    이메일 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        placeholder="example@email.com"
                                    />
                                </div>
                            </div>

                            {/* 예상 가치 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    예상 가치 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={formData.value}
                                        onChange={(e) => handleInputChange('value', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        placeholder="예상 계약 금액 (원)"
                                    />
                                </div>
                            </div>

                            {/* 우선순위 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">우선순위</label>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { value: 'high', label: '높음', color: 'from-red-500 to-red-600' },
                                        { value: 'medium', label: '보통', color: 'from-yellow-500 to-yellow-600' },
                                        { value: 'low', label: '낮음', color: 'from-gray-400 to-gray-500' }
                                    ].map((priority) => (
                                        <button
                                            key={priority.value}
                                            type="button"
                                            onClick={() => handleInputChange('priority', priority.value)}
                                            className={`py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 ${formData.priority === priority.value
                                                ? `bg-gradient-to-r ${priority.color} text-white shadow-lg transform scale-105`
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {priority.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* 키워드 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    키워드 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={formData.keyword}
                                        onChange={(e) => handleInputChange('keyword', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        placeholder="관련 키워드를 입력하세요"
                                    />
                                </div>
                            </div>

                            {/* 관계 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    관계 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                {!isTypingRelation ? (
                                    <div className="flex gap-2">
                                        <select
                                            value={formData.relation || ''}
                                            onChange={(e) => handleInputChange('relation', e.target.value)}
                                            className="flex-1 pl-3 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                        >
                                            <option value="">선택하세요</option>
                                            {relationOptions.map(opt => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => setIsTypingRelation(true)}
                                            className="px-3 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold"
                                        >
                                            직접입력
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={formData.relation}
                                            onChange={(e) => handleInputChange('relation', e.target.value)}
                                            className="flex-1 pl-3 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white"
                                            placeholder="관계를 입력하세요"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setIsTypingRelation(false)}
                                            className="px-3 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold"
                                        >
                                            목록보기
                                        </button>
                                    </div>
                                )}
                            </div>



                            {/* 메모 */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    메모 <span className="text-gray-400 text-xs">(선택)</span>
                                </label>
                                <div className="relative">
                                    <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                    <textarea
                                        value={formData.memo}
                                        onChange={(e) => handleInputChange('memo', e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all duration-300 bg-white resize-y"
                                        placeholder="추가 정보나 특이사항을 입력하세요"
                                        rows="3"
                                    />
                                </div>
                            </div>
                                </div>
                            </details>
                        </form>
                    </div>

                    {/* 고정 버튼 영역 */}
                    <div className="p-6 bg-white border-t border-gray-200 flex-shrink-0">
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-all duration-300"
                            >
                                취소
                            </button>
                            <motion.button
                                type="submit"
                                onClick={handleSubmit}
                                className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 transition-all duration-300 flex items-center justify-center gap-2"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                                title={appStatus === 'expired' && !editingCustomer ? '누적 10명 제한' : ''}
                            >
                                <Save className="w-4 h-4" />
                                {editingCustomer ? '수정' : '추가'}
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default CustomerForm;
